import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Brain, Plus, Upload, FileText, Trash2, MessageSquare, BarChart3,
  Sparkles, Loader2, AlertCircle, Folder, ChevronRight, Link as LinkIcon, Globe,
} from "lucide-react";
import { formatDate } from "@/lib/constants";

const FOLDERS = ["Factsheets", "Brochures", "Presentations", "Reports", "Other"];
const CONFIDENCE_TONE = {
  high: "bg-primary/10 text-primary",
  medium: "bg-accent/10 text-accent",
  low: "bg-destructive/10 text-destructive",
  no_context: "bg-secondary text-muted-foreground",
};

export default function IntelligencePage() {
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWs, setActiveWs] = useState(null);
  const [wsDlg, setWsDlg] = useState(false);
  const [wsForm, setWsForm] = useState({ name: "", kind: "Fund House", description: "" });

  const loadWorkspaces = async () => {
    const { data } = await api.get("/intelligence/workspaces");
    setWorkspaces(data);
    if (data.length && !activeWs) setActiveWs(data[0]);
  };
  useEffect(() => { loadWorkspaces(); /* eslint-disable-next-line */ }, []);

  const createWs = async () => {
    if (!wsForm.name.trim()) { toast.error("Name required"); return; }
    try {
      const { data } = await api.post("/intelligence/workspaces", wsForm);
      toast.success("Workspace created");
      setWsDlg(false);
      setWsForm({ name: "", kind: "Fund House", description: "" });
      setActiveWs(data);
      loadWorkspaces();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deleteWs = async (w) => {
    if (!window.confirm(`Delete workspace "${w.name}" and all its documents?`)) return;
    try {
      await api.delete(`/intelligence/workspaces/${w.id}`);
      toast.success("Deleted");
      if (activeWs?.id === w.id) setActiveWs(null);
      loadWorkspaces();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Brain className="h-5 w-5" />
          </div>
          <div>
            <p className="overline text-primary">Institutional memory</p>
            <h1 className="font-dashboard text-3xl font-bold tracking-tight">Intelligence</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Upload factsheets, brochures, presentations, reports. Ask questions grounded in your own library — never hallucinated.
            </p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="chat">
        <TabsList>
          <TabsTrigger value="chat" data-testid="intel-tab-chat"><MessageSquare className="mr-2 h-3 w-3" /> Chat</TabsTrigger>
          <TabsTrigger value="library" data-testid="intel-tab-library"><FileText className="mr-2 h-3 w-3" /> Library</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="intel-tab-analytics"><BarChart3 className="mr-2 h-3 w-3" /> Analytics</TabsTrigger>
        </TabsList>

        <div className="mt-6 grid gap-6 lg:grid-cols-[240px_1fr]">
          {/* Workspace switcher */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="overline text-muted-foreground">Workspaces</span>
              <Dialog open={wsDlg} onOpenChange={setWsDlg}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid="new-workspace-btn">
                    <Plus className="h-3 w-3" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>New workspace</DialogTitle></DialogHeader>
                  <div className="space-y-3 py-3">
                    <div><Label>Name</Label><Input value={wsForm.name} onChange={(e) => setWsForm({ ...wsForm, name: e.target.value })} placeholder="e.g. HDFC Mutual Fund" data-testid="ws-name-input" /></div>
                    <div>
                      <Label>Kind</Label>
                      <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                        value={wsForm.kind} onChange={(e) => setWsForm({ ...wsForm, kind: e.target.value })} data-testid="ws-kind-select">
                        <option>Fund House</option><option>Asset Class</option>
                      </select>
                    </div>
                    <div><Label>Description</Label><Textarea rows={2} value={wsForm.description} onChange={(e) => setWsForm({ ...wsForm, description: e.target.value })} data-testid="ws-desc-input" /></div>
                  </div>
                  <DialogFooter><Button onClick={createWs} data-testid="ws-save-btn">Create</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            {workspaces.length === 0 && <p className="text-xs text-muted-foreground">No workspaces yet.</p>}
            {workspaces.map((w) => (
              <button
                key={w.id}
                onClick={() => setActiveWs(w)}
                className={`group flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition ${
                  activeWs?.id === w.id ? "border-primary bg-primary/5" : "border-border bg-white hover:bg-secondary/40"
                }`}
                data-testid={`workspace-item-${w.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{w.name}</div>
                  <div className="text-[10px] text-muted-foreground">{w.kind} · {w.document_count} docs</div>
                </div>
                <span
                  onClick={(e) => { e.stopPropagation(); deleteWs(w); }}
                  className="hidden text-muted-foreground hover:text-destructive group-hover:inline-flex"
                  data-testid={`ws-delete-${w.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </span>
              </button>
            ))}
          </div>

          {/* Active workspace views */}
          <div className="min-w-0">
            {!activeWs ? (
              <Card className="border-border bg-white p-10 text-center">
                <Brain className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Create a workspace to start uploading documents.</p>
              </Card>
            ) : (
              <>
                <TabsContent value="chat"><ChatView workspace={activeWs} /></TabsContent>
                <TabsContent value="library"><LibraryView workspace={activeWs} /></TabsContent>
                <TabsContent value="analytics"><AnalyticsView /></TabsContent>
              </>
            )}
          </div>
        </div>
      </Tabs>
    </div>
  );
}

function LibraryView({ workspace }) {
  const [docs, setDocs] = useState([]);
  const [file, setFile] = useState(null);
  const [folder, setFolder] = useState("");
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const [urlFolder, setUrlFolder] = useState("");
  const [addingUrl, setAddingUrl] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/intelligence/workspaces/${workspace.id}/documents`);
    setDocs(data);
  };
  useEffect(() => {
    load();
    // Poll while anything is still processing
    pollRef.current = setInterval(async () => {
      const { data } = await api.get(`/intelligence/workspaces/${workspace.id}/documents`);
      setDocs(data);
      if (!data.some((d) => ["Queued", "Extracting", "Summarising"].includes(d.status))) {
        clearInterval(pollRef.current);
      }
    }, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [workspace.id]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (folder) fd.append("folder", folder);
    try {
      await api.post(`/intelligence/workspaces/${workspace.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded — processing in background");
      setFile(null); setFolder("");
      load();
      // restart poll
      clearInterval(pollRef.current);
      pollRef.current = setInterval(load, 4000);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.original_filename}"?`)) return;
    try { await api.delete(`/intelligence/documents/${d.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const addUrl = async () => {
    const clean = url.trim();
    if (!clean) return;
    setAddingUrl(true);
    try {
      await api.post(`/intelligence/workspaces/${workspace.id}/urls`, {
        url: clean,
        folder: urlFolder || undefined,
      });
      toast.success("URL scraped — processing in background");
      setUrl(""); setUrlFolder("");
      load();
      clearInterval(pollRef.current);
      pollRef.current = setInterval(load, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch URL");
    } finally {
      setAddingUrl(false);
    }
  };

  const grouped = FOLDERS.reduce((acc, f) => {
    acc[f] = docs.filter((d) => (d.folder || "Other") === f);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <Card className="border-border bg-white p-5">
        <h3 className="mb-3 font-dashboard text-sm font-bold">Upload to <span className="text-primary">{workspace.name}</span></h3>
        <div className="grid gap-3 md:grid-cols-[1fr_180px_auto]">
          <Input type="file" accept=".pdf,.docx,.xlsx,.pptx,.txt,.md,.csv,.jpg,.jpeg,.png"
            onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="intel-file-input" />
          <select className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="intel-folder-select">
            <option value="">Auto-classify folder</option>
            {FOLDERS.map((f) => <option key={f}>{f}</option>)}
          </select>
          <Button onClick={upload} disabled={!file || uploading} className="gap-2" data-testid="intel-upload-btn">
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4" /> Upload</>}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Ingestion (extract + chunk + AI summarise + tag) runs in the background — refresh to see progress.</p>
      </Card>

      <Card className="border-border bg-white p-5" data-testid="url-ingest-card">
        <h3 className="mb-3 flex items-center gap-2 font-dashboard text-sm font-bold">
          <Globe className="h-4 w-4 text-accent" />
          Add a webpage to <span className="text-primary">{workspace.name}</span>
        </h3>
        <div className="grid gap-3 md:grid-cols-[1fr_180px_auto]">
          <Input
            type="url"
            placeholder="https://example.com/fund-factsheet"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !addingUrl && addUrl()}
            data-testid="intel-url-input"
          />
          <select
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={urlFolder}
            onChange={(e) => setUrlFolder(e.target.value)}
            data-testid="intel-url-folder-select"
          >
            <option value="">Auto-classify folder</option>
            {FOLDERS.map((f) => <option key={f}>{f}</option>)}
          </select>
          <Button
            onClick={addUrl}
            disabled={!url.trim() || addingUrl}
            className="gap-2"
            data-testid="intel-url-add-btn"
          >
            {addingUrl ? <><Loader2 className="h-4 w-4 animate-spin" /> Fetching…</> : <><LinkIcon className="h-4 w-4" /> Add URL</>}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Static webpages only — the page is scraped, chunked and indexed alongside your file uploads.
        </p>
      </Card>

      {FOLDERS.map((f) => grouped[f].length > 0 && (
        <Card key={f} className="border-border bg-white p-5" data-testid={`folder-${f}`}>
          <div className="mb-3 flex items-center gap-2">
            <Folder className="h-4 w-4 text-primary" />
            <h4 className="font-dashboard text-sm font-bold">{f}</h4>
            <span className="text-xs text-muted-foreground">({grouped[f].length})</span>
          </div>
          <div className="space-y-2">
            {grouped[f].map((d) => (
              <div key={d.id} className="rounded-md border border-border bg-secondary/20 p-3" data-testid={`doc-${d.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {d.source_type === "url"
                        ? <Globe className="h-3 w-3 text-accent" />
                        : <FileText className="h-3 w-3 text-muted-foreground" />}
                      <span className="truncate text-sm font-medium">{d.original_filename}</span>
                      <StatusChip status={d.status} />
                    </div>
                    {d.source_url && (
                      <a
                        href={d.source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-0.5 block truncate text-[11px] text-accent hover:underline"
                        data-testid={`doc-url-${d.id}`}
                      >
                        {d.source_url}
                      </a>
                    )}
                    {d.summary && <p className="mt-1 text-xs text-muted-foreground">{d.summary}</p>}
                    {(d.tags || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {d.tags.slice(0, 8).map((t, i) => (
                          <span key={i} className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] text-accent">{t}</span>
                        ))}
                      </div>
                    )}
                    {d.status === "Failed" && <div className="mt-1 text-xs text-destructive">{d.error}</div>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove(d)} data-testid={`doc-del-${d.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
      {docs.length === 0 && <p className="text-sm text-muted-foreground">No documents yet in this workspace.</p>}
    </div>
  );
}

function StatusChip({ status }) {
  const cls = status === "Ready" ? "bg-primary/10 text-primary"
    : status === "Failed" ? "bg-destructive/10 text-destructive"
    : "bg-accent/10 text-accent";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{status}</span>;
}

function ChatView({ workspace }) {
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    const question = q.trim();
    if (!question) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setQ("");
    setBusy(true);
    try {
      const { data } = await api.post("/intelligence/chat", { workspace_id: workspace.id, question });
      setMessages((m) => [...m, { role: "assistant", ...data }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
      setMessages((m) => [...m, { role: "assistant", answer: "Something went wrong.", citations: [], confidence: 0, confidence_label: "no_context" }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <Card className="border-border bg-white p-5 min-h-[400px] max-h-[65vh] overflow-y-auto" data-testid="chat-panel">
        {messages.length === 0 && (
          <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center">
            <Sparkles className="mb-3 h-8 w-8 text-primary" />
            <p className="font-dashboard text-base font-bold">Ask anything about <span className="text-primary">{workspace.name}</span></p>
            <p className="mt-1 text-sm text-muted-foreground">Grounded in your uploaded documents. Every fact cited, nothing invented.</p>
          </div>
        )}
        <div className="space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              {m.role === "user" ? (
                <div className="max-w-[80%] rounded-2xl bg-primary px-4 py-2.5 text-sm text-primary-foreground" data-testid={`msg-user-${i}`}>{m.text}</div>
              ) : (
                <div className="max-w-[90%] space-y-2" data-testid={`msg-assistant-${i}`}>
                  <div className="rounded-2xl bg-secondary/40 px-4 py-3 text-sm" data-testid={`msg-answer-${i}`}>
                    <MarkdownAnswer text={m.answer} />
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`rounded-full px-2 py-0.5 font-semibold ${CONFIDENCE_TONE[m.confidence_label] || CONFIDENCE_TONE.no_context}`} data-testid={`msg-confidence-${i}`}>
                      {m.confidence_label} confidence · {m.confidence.toFixed(2)}
                    </span>
                    {m.citations.length > 0 && <span className="text-muted-foreground">{m.citations.length} source{m.citations.length !== 1 ? "s" : ""}</span>}
                  </div>
                  {m.citations.length > 0 && (
                    <div className="space-y-1" data-testid={`msg-citations-${i}`}>
                      {m.citations.map((c) => (
                        <details key={c.chunk_number} className="rounded-md border border-border bg-white p-2">
                          <summary className="cursor-pointer text-xs">
                            <span className="mr-2 rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary">[{c.chunk_number}]</span>
                            <span className="font-medium">{c.document_name}</span>
                            {c.folder && <span className="ml-2 text-muted-foreground">· {c.folder}</span>}
                            <span className="ml-2 tabular-nums text-muted-foreground">· {c.score.toFixed(2)}</span>
                          </summary>
                          <p className="mt-2 text-xs text-muted-foreground">{c.snippet}</p>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="flex items-center gap-2 rounded-2xl bg-secondary/40 px-4 py-2.5 text-sm text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Thinking…</div></div>}
        </div>
      </Card>
      <div className="flex gap-2">
        <Input
          placeholder="Ask a question — e.g. Compare Sharpe ratios of the flexi cap funds"
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !busy && ask()}
          className="flex-1" data-testid="chat-input"
        />
        <Button onClick={ask} disabled={!q.trim() || busy} className="gap-1" data-testid="chat-send-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
          Ask
        </Button>
      </div>
    </div>
  );
}

function AnalyticsView() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/intelligence/analytics").then(({ data }) => setData(data)); }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="grid gap-4">
      <div className="grid gap-4 md:grid-cols-4">
        <Stat label="Documents" value={data.total_documents} sub={`${data.documents_ready} ready`} />
        <Stat label="Processing" value={data.documents_processing} sub={data.documents_failed ? `${data.documents_failed} failed` : "healthy"} />
        <Stat label="Workspaces" value={data.total_workspaces} sub="&nbsp;" />
        <Stat label="Questions asked" value={data.total_questions} sub="all time" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border bg-white p-5" data-testid="analytics-popular-topics">
          <h4 className="mb-3 font-dashboard text-sm font-bold">Popular topics</h4>
          {data.popular_topics.length === 0 && <p className="text-sm text-muted-foreground">Upload docs to build the tag index.</p>}
          <div className="flex flex-wrap gap-1.5">
            {data.popular_topics.map((t) => (
              <span key={t.tag} className="rounded-full bg-accent/10 px-2 py-0.5 text-xs text-accent">
                {t.tag} <span className="text-[10px] text-muted-foreground">· {t.count}</span>
              </span>
            ))}
          </div>
        </Card>

        <Card className="border-border bg-white p-5" data-testid="analytics-knowledge-gaps">
          <h4 className="mb-3 flex items-center gap-2 font-dashboard text-sm font-bold">
            <AlertCircle className="h-4 w-4 text-destructive" /> Knowledge gaps
          </h4>
          {data.knowledge_gaps.length === 0 && <p className="text-sm text-muted-foreground">No unanswered / low-confidence questions yet.</p>}
          <ul className="space-y-2 text-sm">
            {data.knowledge_gaps.map((g, i) => (
              <li key={i} className="rounded-md border border-border p-2">
                <div className="text-sm">{g.question}</div>
                <div className="text-[10px] text-muted-foreground">{formatDate(g.created_at)} · confidence {(g.confidence || 0).toFixed(2)}</div>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card className="border-border bg-white p-5" data-testid="analytics-recent">
        <h4 className="mb-3 font-dashboard text-sm font-bold">Recent queries</h4>
        {data.recent_queries.length === 0 && <p className="text-sm text-muted-foreground">No queries yet.</p>}
        <ul className="space-y-1 text-sm">
          {data.recent_queries.map((r, i) => (
            <li key={i} className="flex items-center justify-between rounded-md border border-border p-2">
              <span>{r.question}</span>
              <span className={`ml-3 rounded-full px-2 py-0.5 text-[10px] font-semibold ${CONFIDENCE_TONE[r.confidence_label] || CONFIDENCE_TONE.no_context}`}>{r.confidence_label}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <Card className="border-border bg-white p-4">
      <div className="overline text-muted-foreground">{label}</div>
      <div className="mt-1 font-dashboard text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-[10px] text-muted-foreground" dangerouslySetInnerHTML={{ __html: sub }} />
    </Card>
  );
}

// ---- Markdown renderer for RAG answers ----
// Handles GFM tables — critical for fund comparisons — plus lists, bold, code.
// Every renderer is Tailwind-styled so tables look native to the Reliserve UI.
function MarkdownAnswer({ text }) {
  if (!text) return null;
  return (
    <div className="prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          table: ({ node, ...props }) => (
            <div className="my-3 overflow-x-auto rounded-md border border-border">
              <table className="w-full border-collapse text-xs" data-testid="rag-answer-table" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-primary/5" {...props} />,
          th: ({ node, ...props }) => (
            <th className="border-b border-border px-3 py-2 text-left font-semibold text-primary" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="border-b border-border/60 px-3 py-2 align-top tabular-nums" {...props} />
          ),
          tr: ({ node, ...props }) => <tr className="odd:bg-white even:bg-secondary/20" {...props} />,
          code: ({ inline, ...props }) =>
            inline ? (
              <code className="rounded bg-secondary/60 px-1 py-0.5 text-[11px]" {...props} />
            ) : (
              <pre className="my-2 overflow-x-auto rounded-md bg-secondary/60 p-3 text-[11px]"><code {...props} /></pre>
            ),
          a: ({ node, ...props }) => (
            <a className="text-accent underline hover:text-primary" target="_blank" rel="noreferrer" {...props} />
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
